import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BookOpenIcon, KeyIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { track, EVENTS } from '../services/analytics';
import { postSignInDestination } from '../utils/postSignIn';
import useGoogleSignIn from '../hooks/useGoogleSignIn';
import SigningInOverlay from './SigningInOverlay';
import { START_FREE } from '../content/ctaLabels';

// Compact marketing-style header used by every public route the
// prospective-user funnel touches (LandingPage and the public Resources
// pages). The visual layout — fixed top, gradient book-and-key logo,
// pill-style nav links, dark green CTA — is the brand for the marketing
// surface; flipping between Landing and Resources with the same chrome
// makes the transition feel continuous instead of "you've left the
// website."
//
// Auth-aware on the right: logged-out visitors see "Start free" (kicks
// off the Google sign-in popup); signed-in visitors see "Open App"
// (deep-links into the launchpad).
//
// `rightSlot` lets a host page inject an extra element between the nav
// links and the CTA — Pricing uses it for the credits chip when the user
// is signed in. Optional; everything works without it.
//
// The signed-in shell of the actual app is the heavier `Navigation`
// component — that one's used inside `AppLayout` and on Resources pages
// for signed-in users. This header is specifically for the
// brochure-side experience.

const MarketingHeader = ({ rightSlot = null }) => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const isActive = (path) =>
        path === '/resources' || path === '/news' || path === '/colleges'
            ? location.pathname.startsWith(path)
            : location.pathname === path;

    const { signIn: handleSignIn, phase: signInPhase } = useGoogleSignIn();

    // `entry`: the landing watch's id for the button that started sign-in.
    const handleGetStarted = (entry) => {
        if (currentUser) {
            postSignInDestination(currentUser.email).then(navigate);
        } else {
            handleSignIn(entry);
        }
    };

    return (
        <>
        <SigningInOverlay phase={signInPhase} />
        <header className="fixed top-0 left-0 right-0 z-50 bg-[#FDFCF7]/80 backdrop-blur-lg border-b border-[#E0DED8]/50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    <Link to="/" aria-label="Stratia Admissions, home" className="flex items-center gap-2 group">
                        {/* Book & Key Logo — matches app brand */}
                        <div className="relative w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-[#1A4D2E] to-[#2D6B45] shadow-md group-hover:shadow-lg transition-shadow">
                            <BookOpenIcon
                                className="w-5 h-5 text-white absolute"
                                style={{ top: '8px', left: '8px' }}
                            />
                            <KeyIcon
                                className="w-4 h-4 text-[#D6E8D5] absolute"
                                style={{ bottom: '6px', right: '6px' }}
                            />
                        </div>

                        {/* Brand text */}
                        <div className="flex items-baseline gap-1">
                            {/* Under 440px the book-and-key mark stands alone, so the
                                phone row (Pricing, News, Colleges, Start free) keeps a
                                16px gutter (design review, #2518; News joined the row,
                                #2575, measured from 360 to 767). */}
                            <span className="hidden font-serif text-xl font-semibold text-[#1A4D2E] min-[440px]:inline">
                                Stratia
                            </span>
                            {/* Under 520px the word gives its room to the phone
                                row (Pricing, News, Colleges, Start free on one line). */}
                            <span className="hidden font-sans text-sm font-medium text-[#4A4A4A] min-[520px]:inline">
                                Admissions
                            </span>
                        </div>
                    </Link>

                    <nav className="hidden md:flex items-center gap-8">
                        {/* Every product screen (decision A, 2026-09-29): desktop
                            only; the phone row keeps Pricing, News, Colleges
                            and Start free. Phones reach the
                            tour from the landing gallery and the footer. */}
                        <Link
                            to="/tour"
                            data-cta="header_tour"
                            className={`text-sm font-medium transition-colors ${
                                isActive('/tour')
                                    ? 'text-[#1A4D2E]'
                                    : 'text-[#4A4A4A] hover:text-[#1A4D2E]'
                            }`}
                        >
                            Tour
                        </Link>
                        {/* Colleges (founder, 2026-09-30, #2626): the section that
                            grows to every school in the catalog. */}
                        <Link
                            to="/colleges"
                            data-cta="header_colleges"
                            className={`text-sm font-medium transition-colors ${
                                isActive('/colleges')
                                    ? 'text-[#1A4D2E]'
                                    : 'text-[#4A4A4A] hover:text-[#1A4D2E]'
                            }`}
                        >
                            Colleges
                        </Link>
                        {/* News (founder pick A, 2026-09-30, #2575): its own
                            section between Tour and Resources. */}
                        <Link
                            to="/news"
                            data-cta="header_news"
                            className={`text-sm font-medium transition-colors ${
                                isActive('/news')
                                    ? 'text-[#1A4D2E]'
                                    : 'text-[#4A4A4A] hover:text-[#1A4D2E]'
                            }`}
                        >
                            News
                        </Link>
                        <Link
                            to="/resources"
                            data-cta="header_resources"
                            className={`text-sm font-medium transition-colors ${
                                isActive('/resources')
                                    ? 'text-[#1A4D2E]'
                                    : 'text-[#4A4A4A] hover:text-[#1A4D2E]'
                            }`}
                        >
                            Resources
                        </Link>
                        <Link
                            to="/pricing"
                            data-cta="header_pricing"
                            className={`text-sm font-medium transition-colors ${
                                isActive('/pricing')
                                    ? 'text-[#1A4D2E]'
                                    : 'text-[#4A4A4A] hover:text-[#1A4D2E]'
                            }`}
                        >
                            Pricing
                        </Link>
                        {/* The counselor door (session-insights HIGH,
                            2026-09-01): a counselor arriving from outreach
                            saw only student CTAs and stalled. One visible
                            link, above the fold, on every marketing page.
                            It opens the public counselor page, whose own CTA
                            leads to the /counselor sign-in (#2682: the link
                            used to drop a stranger straight onto the sign-in
                            form, with nothing about what it signs into). */}
                        <Link
                            to="/for-schools"
                            data-testid="header-counselor-door"
                            data-cta="header_counselor"
                            onClick={() => track(EVENTS.COUNSELOR_DOOR_CLICKED, { source: 'header' })}
                            className={`text-sm font-medium transition-colors ${
                                isActive('/for-schools') || isActive('/counselor')
                                    ? 'text-[#1A4D2E]'
                                    : 'text-[#4A4A4A] hover:text-[#1A4D2E]'
                            }`}
                        >
                            For counselors
                        </Link>
                        {rightSlot}
                        {currentUser ? (
                            <Link
                                to="/launchpad"
                                data-cta="header_open_app"
                                className="px-4 py-2 bg-[#1A4D2E] text-white text-sm font-medium rounded-lg hover:bg-[#2D6B45] transition-all"
                            >
                                Open App
                            </Link>
                        ) : (
                            <button
                                data-cta="header_get_started"
                                onClick={() => handleSignIn('header_get_started')}
                                className="px-4 py-2 bg-[#1A4D2E] text-white text-sm font-medium rounded-lg hover:bg-[#2D6B45] transition-all"
                            >
                                {START_FREE}
                            </button>
                        )}
                    </nav>

                    {/* Mobile CTA. Not a "fallback" — on a phone the hero's own
                        buttons sit below the fold (measured 2026-08-07: y=1089
                        against an 844px viewport), so this is the ONLY action a
                        first-time mobile visitor sees before scrolling, and 95%
                        of them arrive on a phone. Hence the 44px tap floor and
                        the same words the page uses everywhere else. */}
                    <div className="md:hidden flex items-center gap-0.5">
                        {/* Phones get Pricing (founder pick A, 2026-09-29: the
                            phone header had no way to reach it), News and
                            Colleges. The counselor door left the phone row on
                            2026-09-30 (founder, #2626): counselors arriving from
                            InMail on a phone reach it from the footer. 44px tap
                            floor. */}
                        <Link
                            to="/pricing"
                            data-testid="header-pricing-mobile"
                            data-cta="header_pricing"
                            className="inline-flex min-h-[44px] items-center px-1 text-sm font-medium text-[#1A4D2E] max-[339px]:px-0.5 max-[339px]:text-[13px] min-[390px]:px-1.5"
                        >
                            Pricing
                        </Link>
                        {/* News rides the phone row too (pick A): Pricing,
                            News, Colleges, Start free fit one row down to
                            320px with a 16px gutter: under 390px the padding
                            tightens, under 340px the text is 13px (#2626). */}
                        <Link
                            to="/news"
                            data-testid="header-news-mobile"
                            data-cta="header_news"
                            className="inline-flex min-h-[44px] items-center px-1 text-sm font-medium text-[#1A4D2E] max-[339px]:px-0.5 max-[339px]:text-[13px] min-[390px]:px-1.5"
                        >
                            News
                        </Link>
                        {/* Founder, 2026-09-30: (remove Counselors from the phone row)
                            Colleges takes the slot; For counselors stays on
                            desktop and in the footer. */}
                        <Link
                            to="/colleges"
                            data-testid="header-colleges-mobile"
                            data-cta="header_colleges"
                            className="inline-flex min-h-[44px] items-center px-1 text-sm font-medium text-[#1A4D2E] max-[339px]:px-0.5 max-[339px]:text-[13px] min-[390px]:px-1.5"
                        >
                            Colleges
                        </Link>
                        <button
                            data-cta="header_start_free"
                            onClick={() => handleGetStarted('header_start_free')}
                            className="ml-0.5 inline-flex min-h-[44px] items-center whitespace-nowrap rounded-lg bg-[#1A4D2E] px-2.5 text-sm font-semibold text-white max-[339px]:px-2 max-[339px]:text-[13px] min-[390px]:ml-1 min-[390px]:px-3.5"
                        >
                            {START_FREE}
                        </button>
                    </div>
                </div>
            </div>
        </header>
        </>
    );
};

export default MarketingHeader;
